<!--@format-->
<template>
    <div class="gst-order-confirmation-payment d-flex">
        <div class="gst-order-confirmation-payment__summary d-flex">
            <span>{{ $t('paymentSummary') }}</span>
            <span
                tabindex="0"
                @click="showMoreDetails = !showMoreDetails"
                @keypress.enter="showMoreDetails = !showMoreDetails"
                >{{ $t(!showMoreDetails ? 'viewDetails' : 'closeDetails') }}</span
            >
        </div>
        <div class="gst-order-confirmation-payment__method d-flex">
            <div>
                <div class="gst-payment-info d-flex">
                    <span>{{ $t('paymentMethod') }}</span>
                    <span class="gst-payment-info__total"
                        >{{ $t('total') }}: {{ totalPrice | currencyFilter(currency) }}
                    </span>
                </div>
                <div
                    class="card-info"
                    :class="{
                        'd-flex justify-space-between': isPackageOffer,
                    }">
                    <div class="d-flex align-center">
                        {{ paymentInfo.card.type }}
                        <div class="card-info__separator"></div>
                        {{ paymentInfo.card.last4 }}
                    </div>
                    <span
                        v-if="isPackageOffer"
                        class="card-info__sales_final">
                        {{ $t('allSalesFinal') }}
                    </span>
                </div>
            </div>
            <div
                v-if="redeemedPoints > 0"
                class="gst-redeem-info d-flex justify-space-between">
                <span>{{ $t('pointsRedeemed') }}</span>
                <span>{{
                    $t('shared:common.pointWithCount', {
                        count: redeemedPoints,
                        formattedCount: formattedPoints,
                    })
                }}</span>
            </div>
        </div>
        <v-expand-transition>
            <div
                v-if="showMoreDetails"
                class="gst-order-confirmation-payment__details">
                <h5 class="mt-4 mb-2">
                    {{ titlePaymentItems }}
                </h5>
                <div class="detail__item d-flex flex-wrap align-end">
                    <div class="detail__item-row">
                        <div class="detail__item-name flex-grow-1 u-text-transform-by-language">
                            <div
                                v-for="(item, key) in paymentItems"
                                :key="key"
                                class="mb-0">
                                <span class="mr-1">{{ item.name }}</span>
                                <span>x {{ item.quantity }}</span>
                            </div>
                        </div>
                        <div class="detail__item-price flex-shrink-0">
                            {{ totalBasePrice | currencyFilter(currency) }}
                        </div>
                    </div>
                    <div
                        v-if="totalIncludedFeeAmount"
                        class="detail__item-row-variant-2 mt-2">
                        <div class="detail__item-name">
                            <span>{{ $t('_common:terms.feesIncluded') }}</span>
                        </div>
                        <div class="detail__item-price flex-shrink-0">
                            {{ totalIncludedFeeAmount | currencyFilter(currency) }}
                        </div>
                    </div>
                </div>
                <div v-if="fees.length || !isOpenInventoryOrDynamic">
                    <h5 class="mt-4 mb-2">
                        {{ $t('fees') }}
                    </h5>
                    <div
                        v-if="hasFees"
                        class="detail__item d-flex flex-wrap align-end">
                        <div
                            v-if="fees.length"
                            class="detail__item-row">
                            <div
                                v-for="(item, key) in fees"
                                :key="key"
                                class="d-flex flex-grow-1 align-center justify-between u-width-100">
                                <div
                                    class="detail__item-name flex-grow-1 u-text-transform-by-language">
                                    <div
                                        v-if="
                                            !item.isOrderFee ||
                                            (item.isOrderFee && item.quantity > 1)
                                        ">
                                        <span class="mr-1">{{ item.name }}:</span>
                                        <span class="mr-1">{{
                                            item.price | currencyFilter(currency)
                                        }}</span>
                                        <span>x {{ item.quantity }}</span>
                                    </div>
                                    <div v-else>
                                        {{ item.name }}
                                    </div>
                                </div>
                                <div class="detail__item-price flex-shrink-0">
                                    {{ item.amount | currencyFilter(item.currency) }}
                                </div>
                            </div>
                        </div>
                        <div
                            v-if="totalPayLaterAmount && !isOpenInventoryOrDynamic"
                            class="detail__item-row-variant-2 mt-4 mt-md-2">
                            <div class="detail__item-name">
                                <span>{{ $t('_common:terms.feesNotIncluded') }}</span>
                                <BaseTooltip
                                    bottom
                                    :text="feesHotelsNotIncludedTooltipContent"
                                    class-content="gst-order-confirmation-payment__tooltip-content"
                                    class="gst-order-confirmation-payment__tooltip">
                                    <BaseIcon
                                        symbol-id="icons--help"
                                        :width="18"
                                        :height="18" />
                                </BaseTooltip>
                            </div>
                            <div class="detail__item-price flex-shrink-0">
                                {{ totalPayLaterAmount | currencyFilter(currency) }}
                            </div>
                        </div>
                    </div>
                    <div
                        v-else
                        class="detail__item d-flex flex-row align-end">
                        ---
                    </div>
                </div>
                <div
                    v-if="totalPayLaterAmount && isOpenInventoryOrDynamic"
                    class="gst-payment-info d-flex pt-4 pb-2">
                    <h5 class="d-flex justify-start align-start">
                        {{ $t('_common:terms.payAtProperty') }}
                        <BaseTooltip
                            top
                            :text="feesHotelsNotIncludedEnum"
                            class-content="gst-payment-info__tooltip-content"
                            class="gst-payment-info__tooltip"
                            class-activator="gst-payment-info__tooltip-activator">
                            <BaseIcon
                                symbol-id="icons--help"
                                :width="18"
                                :height="18" />
                        </BaseTooltip>
                    </h5>
                    <div class="detail__item-price">
                        {{ totalPayLaterAmount | currencyFilter(currency) }}
                    </div>
                </div>
                <template v-if="shippingInformation">
                    <h5 class="mt-4 mb-2">
                        {{ $t('delivery') }}
                    </h5>
                    <div class="d-flex flex-row align-end">
                        <div class="detail__item-name flex-grow-1">
                            {{ shippingInformation.name }}
                        </div>
                        <div class="detail__item-price flex-shrink-0">
                            {{ shippingInformation.price | currencyFilter(currency) }}
                        </div>
                    </div>
                </template>
            </div>
        </v-expand-transition>
    </div>
</template>

<script>
import feeConstants from '@core/utils/constants/fee';
import shippingFilter from '@core/filters/localFilters/shipping';
import FeesContainerMixin from '../TheCheckout/Mixins/FeesContainerMixin';

export default {
    name: 'OrderConfirmationPaymentInfo',
    mixins: [FeesContainerMixin],
    props: {
        currency: {
            type: String,
            required: true,
        },
        paymentItems: {
            type: Array,
            required: true,
        },
        hasHotelReservationOnly: {
            type: Boolean,
            required: true,
        },
        isPackageOffer: {
            type: Boolean,
            default: false,
        },
        isOpenInventoryOrDynamic: {
            type: Boolean,
            default: false,
        },
        order: {
            type: Object,
            required: true,
        },
    },
    data: function () {
        return {
            showMoreDetails: false,
        };
    },
    i18nOptions: {
        namespaces: 'main',
        keyPrefix: 'views.cart.theOrderConfirmation',
    },
    filters: {
        shippingFilter,
    },
    computed: {
        totalPrice() {
            return this.redeemedPoints > 0
                ? Number(this.order.paymentCash)
                : this.order.totalAmount;
        },
        redeemedPoints() {
            return Number(this.order.paymentPoints);
        },
        totalBasePrice() {
            return this.order.items.reduce((acc, item) => {
                return acc + item.price;
            }, 0);
        },
        paymentInfo() {
            return this.order.paymentInfo;
        },
        shippingInformation() {
            const itemWithShipping = this.order.items.find((item) => item.shipping);
            if (!itemWithShipping) {
                return false;
            }
            const { charges, shipping } = itemWithShipping;
            const price = charges.find((charge) => charge.type === feeConstants.TYPES.DELIVERY).fee;

            return {
                name: shipping.short,
                price,
            };
        },
        titlePaymentItems: function () {
            if (this.hasHotelReservationOnly) {
                return this.$t('_common:terms.hotelAddOn');
            }
            return this.$t('_common:terms.bundleTicketsHotel');
        },
        formattedPoints() {
            return this.$options.filters.bonusPoints(this.redeemedPoints);
        },
    },
};
</script>

<style lang="scss">
@import '@scssMixins';
@import '@scssVariables';

.gst-order-confirmation-payment {
    width: 100%;
    align-items: center;
    justify-content: space-between;
    flex-direction: column;

    .gst-payment-info {
        line-height: line-height('xxxl');
        font-size: font-size('l');
        font-weight: font-weight('bold');
        align-items: center;
        justify-content: space-between;

        .gst-payment-info__tooltip-activator {
            margin-top: 2px;
            margin-left: theme-spacing(2);
        }
    }

    .gst-order-confirmation-payment__summary {
        width: 100%;
        line-height: line-height('xxl');
        padding-bottom: theme-spacing(4);
        font-size: font-size('m');
        font-weight: font-weight('bold');
        align-items: center;
        justify-content: space-between;
        border-bottom: 1px solid #e3e3e3;

        span:nth-child(2) {
            color: theme-color('primary') !important;
            cursor: pointer;
        }
    }

    .gst-order-confirmation-payment__method {
        width: 100%;
        flex-direction: column;
        gap: theme-spacing(4);

        .payment-info {
            line-height: line-height('xxxl');
            font-size: font-size('l');
            font-weight: font-weight('bold');
            align-items: center;
            justify-content: space-between;
        }

        .card-info {
            display: flex;
            line-height: line-height('m');
            margin-top: theme-spacing(2);
            color: #999999;
            font-size: font-size('xxs');
            font-weight: font-weight('bold');
            text-transform: uppercase;
            align-items: center;

            .card-info__separator {
                width: 23px;
                margin: theme-spacing(0) theme-spacing(1);
                border-bottom: 1px solid #999999;
            }

            .card-info__sales_final {
                line-height: line-height('m');
                font-size: font-size('xxs');
                font-weight: font-weight('regular');
                text-transform: none;
            }
        }

        .gst-redeem-info {
            font-size: font-size('xxs');
            font-weight: font-weight('medium');

            @include desktop-only {
                font-size: font-size('s');
            }
        }
    }

    .gst-order-confirmation-payment__details {
        width: 100%;
        padding-bottom: theme-spacing(6);
        border-top: 1px solid #e3e3e3;

        h5 {
            line-height: line-height('xxxl');
            color: theme-color('quaternary');
            font-size: font-size('l');
            font-weight: font-weight('large');
        }

        .detail__item {
            padding-bottom: theme-spacing(6);
            border-bottom: 1px solid #e3e3e3;
        }

        .detail__item-name,
        .detail__item-price {
            line-height: line-height('xl');
            color: theme-color('quaternary');
            font-size: font-size('s');
            font-weight: font-weight('regular');
        }

        .detail__item-row {
            display: flex;
            width: 100%;
            flex-wrap: wrap;
        }

        .detail__item-row-variant-2 {
            display: flex;
            flex-grow: 1;

            .detail__item-name {
                display: flex;
                line-height: line-height('m');
                padding-right: theme-spacing(2);
                color: theme-color('tertiary');
                font-size: font-size('xxs');
                flex-direction: row;
                flex-grow: 1;

                span {
                    margin-right: theme-spacing(4);
                }

                svg {
                    height: 18px;
                    width: 18px;

                    .gst-svg-icon {
                        stroke: theme-color('tertiary') !important;
                    }

                    .gst-svg-icon:nth-child(2) {
                        fill: transparent !important;
                    }
                }
            }

            .detail__item-price {
                line-height: line-height('m');
                color: theme-color('tertiary');
                font-size: font-size('xxs');
            }
        }
    }
}

.gst-order-confirmation-payment__tooltip-content.v-tooltip__content,
.gst-payment-info__tooltip-content.v-tooltip__content {
    max-width: 316px !important;
}
</style>
